import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate } from 'react-router-dom';
import { LanguageChoice, ThemeChoice, UserMenu } from '@easydent/shared-ui';
import { useAuthStore } from '@/stores/authStore';
import { logout } from '@/lib/api';


const LANGUAGES = [
  { code: 'ro', label: 'Română' },
  { code: 'en', label: 'English' },
];

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Inside the component so the labels re-resolve on a language change; as a module-level
  // constant they are captured once, in whatever language loaded first.
  const nav = [
    { to: '/', label: t('nav.dashboard', 'Dashboard'), end: true },
    { to: '/tenants', label: t('tenants.title', 'Tenants'), end: false },
    { to: '/plans', label: t('plans.title', 'Plans'), end: false },
    { to: '/suppliers', label: t('suppliers.title', 'Suppliers'), end: false },
    { to: '/creatives', label: t('nav.creatives', 'Creatives'), end: false },
    { to: '/billing', label: t('billing.title', 'Billing'), end: false },
    { to: '/demand', label: t('demand.title', 'Demand'), end: false },
    { to: '/operators', label: t('operators.title', 'Operators'), end: false },
    { to: '/settings', label: t('nav.settings', 'Settings'), end: false },
  ];
  const { userName, clearAuth } = useAuthStore();

  const signOut = async () => {
    try { await logout(); } catch { /* ignore */ }
    clearAuth();
    navigate('/login', { replace: true });
  };

  const preferences = (
    <>
      <ThemeChoice labels={{
        label: t('theme.label', 'Theme'), light: t('theme.light', 'Light'),
        dark: t('theme.dark', 'Dark'), system: t('theme.system', 'Auto'),
      }} />
      <LanguageChoice
        value={i18n.language}
        languages={LANGUAGES}
        onChange={(code) => i18n.changeLanguage(code)}
        label={t('language', 'Language')}
      />
    </>
  );

  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-surface">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-3">
          <div className="flex items-center gap-2 font-bold text-brand-ink-strong">
            <span className="text-lg">⬡</span> easyDent <span className="font-normal text-subtle-ink">{t('portalName', "Operator")}</span>
          </div>
          <nav className="hidden gap-1 text-sm sm:flex">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-1.5 font-medium ${isActive ? 'bg-brand-50 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
              <UserMenu
                name={userName}
                items={[]}
                preferences={preferences}
                onLogout={signOut}
                logoutLabel={t('signOut', 'Sign out')}
              />
            </div>
        </div>

        {/* Mobile: the nav gets its own scrollable row rather than a hamburger. With a handful of
            destinations, hiding them behind a tap costs more than it saves. */}
        <nav className="flex gap-1 overflow-x-auto border-t border-line-soft px-4 py-2 text-sm sm:hidden">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `shrink-0 rounded-lg px-3 py-1 font-medium ${isActive ? 'bg-brand-50 text-brand-ink-strong' : 'text-muted hover:bg-canvas'}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-6">{children}</main>
    </div>
  );
};

export default Layout;
